import { I18n } from "@assets/strings";
import moment from "moment";
import React from "react";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { Livecourse, LivecourseStatus, LivecourseType } from "@domain/livecourse/livecourse";
import { SharedLive } from "@domain/post/post";
import { formatTimeAgo } from "@utils/date";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { JoinLiveButton } from "@ui/pages/livecourses/livecoursesThumbnail/joinButton";
import { gap } from "@ui/styles/utils";
import { CalendarIcon, TimeIcon } from "@ui/components/icons";
import { Row } from "@ui/components/layout";
import { AuthLink } from "@ui/components/linkExternal";
import { LivecourseHeader } from "./livecourseHeader";

interface LivecourseThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	livecourse: Livecourse;
	post: SharedLive;
}
export const LivecourseThumbnail: React.FC<LivecourseThumbnailProps> = ({ livecourse, post, ...htmlProps }) => {
	const starthour = moment(livecourse.startDate).format("hh:mma");
	const endhour = moment(livecourse.endDate).format("hh:mma");
	return (
		<Container {...htmlProps}>
			<LivecourseHeader livecourse={livecourse} />
			<LivecourseContainer>
				<GroupeName>
					{post.shareroom ? (
						<div>
							<AuthLink to={Path.Shareroom(post.shareroom)}>{post.shareroom?.name}</AuthLink>
						</div>
					) : post.reference?.author ? (
						<div>
							<AuthLink to={Path.Organization(post.reference.author)}>{post.reference.author.primaryName}</AuthLink>
						</div>
					) : null}
				</GroupeName>
				<LivecourseName>{livecourse.title}</LivecourseName>
				<LiveDescription>{livecourse.description}</LiveDescription>
				{livecourse && livecourse.status === LivecourseType.SCHEDULED ? (
					<DateContent>
						<Row gap={10} align="center">
							<BlueVerticalBox />
							<Row align="center">
								<Row gap={10} align="center">
									<Row align="center">
										<TimeIcon color="black" />
										<DateElementTitle style={{ paddingLeft: 5 }}>
											{starthour} à {endhour}
										</DateElementTitle>
									</Row>
									<Row align="center">
										<CalendarIcon width={18} height={18} />
										<DateElementTitle>{moment(livecourse.startDate).format("DD/MM/YY")}</DateElementTitle>
									</Row>
								</Row>
							</Row>
						</Row>
					</DateContent>
				) : (
					<DateContent>
						<Row gap={10} align="center">
							<RedVerticalBox />
							<Row align="center">
								<Row gap={0} align="center">
									{livecourse.status === LivecourseStatus.STATUS ? (
										<DateElementTitle>{I18n.t("live.closed")}</DateElementTitle>
									) : (
										<DateElementTitle>
											{I18n.t("live.startedat")} {formatTimeAgo(livecourse.startDate)}
										</DateElementTitle>
									)}
								</Row>
							</Row>
						</Row>
					</DateContent>
				)}
				{livecourse.status === LivecourseStatus.STATUS ? null : (
					<LivecourseButtonJoin>
						<JoinLiveButton livecourse={livecourse} />
					</LivecourseButtonJoin>
				)}
			</LivecourseContainer>
		</Container>
	);
};

const Container = styled.div`
	position: relative;
	border: solid 1px #eaebef;
	border-radius: 13px;
`;

const GroupeName = styled.div`
	color: ${({ theme }) => theme.colors.vividBlue};
	font-family: Sora;
	font-style: normal;
	font-weight: 700;
	font-size: 12px;
	line-height: 13px;
	margin-top: 20px;
	/* margin-left: 16px ; */
	margin-bottom: 10px;
`;

const LiveDescription = styled.div`
	color: ${({ theme }) => theme.colors.liveDescrription};
	/* font-family: 'Sora'; */
	font-style: normal;
	font-weight: normal;
	font-size: 13px;
	line-height: 20px;
	margin-top: 10px;
	margin-bottom: 10px;
	margin-right: 100px;
	@media (max-width: 900px) {
		margin-right: 10px;
	}
	@media (max-width: 1024px) {
		margin-right: 10px;
	}
`;

const LivecourseName = styled.div`
	color: ${({ theme }) => theme.colors.black};
	font-family: Sora;
	font-style: normal;
	font-weight: 600;
	font-size: 14px;
	line-height: 18px;
`;

const LivecourseButtonJoin = styled.div`
	margin-bottom: 20px;
`;

const DateContent = styled(RoundedBlock)`
	display: flex;
	flex: 1;
	flex-direction: row;
	align-items: center;
	${gap(20)};
	border-color: "red";
	margin-top: 12px;
	margin-bottom: 13px;
	background-color: ${({ theme }) => theme.colors.simplegray};
	height: 41px;
	width: 285px;
	@media (max-width: 900px) {
		width: 270px;
	}
	@media (max-width: 1024px) {
		width: 270px;
	}
	margin-right: 5px;
	border-radius: 7px;
	/* margin-left: 16px ; */
`;

const BlueVerticalBox = styled.div`
	position: relative;
	left: 3;
	top: 0;
	height: 37px;
	width: 10px;
	background: ${({ theme }) => theme.colors.secondary};
	border: 1px solid #f5f3f3;
	box-sizing: border-box;
	border-radius: 87px;
`;

const RedVerticalBox = styled.div`
	position: relative;
	left: 3;
	top: 0;
	height: 37px;
	width: 10px;
	background: #e83636;
	border: 1px solid #f5f3f3;
	box-sizing: border-box;
	border-radius: 87px;
`;

const DateElementTitle = styled.div`
	position: relative;
	font-family: Sora;
	color: #4b4b4b;
	font-size: 12px;
	font-weight: 200;
`;

const LivecourseContainer = styled.div`
	display: flex;
	flex-direction: column;
	position: relative;
	background-color: white;
	margin-left: 12px;
	border-bottom-left-radius: 13px;
	border-bottom-right-radius: 13px;
`;
